import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, Shield, Zap, TrendingUp, RefreshCw, CheckCircle } from 'lucide-react';
import { ProTrackMark, StackLogo } from '../components/ProTrackLogo';
import toast from 'react-hot-toast';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

// ─── OTP SCREEN ──────────────────────────────────────────────
function OTPVerify({ email, onSuccess, onBack }) {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const { setSession } = useAuth();

  const handleChange = (i, val) => {
    if (!/^[0-9]?$/.test(val)) return;
    const next = [...otp];
    next[i] = val;
    setOtp(next);
    if (val && i < 5) document.getElementById('otp-' + (i + 1))?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) {
      document.getElementById('otp-' + (i - 1))?.focus();
    }
  };

  const handleVerify = async () => {
    const code = otp.join('');
    if (code.length !== 6) { toast.error('Enter all 6 digits'); return; }
    setLoading(true);
    try {
      const res = await fetch(
        `${API}/auth/verify-otp?email=${encodeURIComponent(email)}&otp=${code}`,
        { method: 'POST', credentials: 'include' }   // include: the httpOnly refresh cookie is cross-origin
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Invalid OTP');
      localStorage.setItem('access_token', data.access_token);
      // refresh_token is set as httpOnly cookie by the backend — do NOT store in localStorage
      localStorage.setItem('user', JSON.stringify(data.user));
      if (data.user?.role) localStorage.setItem('user_role', data.user.role);
      // The server only issues tokens here, after the code was accepted (login() itself never
      // hands out a session while a code is owed), so this is where the visitor becomes signed in.
      setSession(data.user);
      toast.success('Verified! Welcome back.');
      onSuccess(data);
    } catch (err) {
      toast.error(err.message || 'Invalid OTP');
      setOtp(['', '', '', '', '', '']);
      document.getElementById('otp-0')?.focus();
    }
    setLoading(false);
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const res = await fetch(
        `${API}/auth/send-otp?email=${encodeURIComponent(email)}`,
        { method: 'POST' }
      );
      const data = await res.json();
      toast.success('New OTP sent! Check your email.');
      if (data.otp_for_dev) console.log('DEV OTP:', data.otp_for_dev);
      setOtp(['', '', '', '', '', '']);
      document.getElementById('otp-0')?.focus();
    } catch { toast.error('Failed to resend'); }
    setResending(false);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#080d1a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>
      <div style={{ width: '100%', maxWidth: '420px', background: 'rgba(232,234,240,0.03)', border: '1px solid rgba(232,234,240,0.08)', borderRadius: '20px', padding: '40px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '32px', cursor: 'pointer' }} onClick={onBack}>
          <ProTrackMark size={30} />
          <div style={{ fontSize: '18px', fontWeight: '800', color: '#fff' }}>ProTrack<span style={{ color: '#00d4a1' }}>NG</span></div>
        </div>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ width: '60px', height: '60px', background: 'rgba(0,212,161,0.08)', border: '1px solid rgba(0,212,161,0.2)', borderRadius: '16px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
            <Shield size={28} color="#00d4a1" />
          </div>
          <h2 style={{ fontSize: '22px', fontWeight: '800', color: '#fff', marginBottom: '8px' }}>Check your email</h2>
          <p style={{ color: 'rgba(232,234,240,0.5)', fontSize: '13px', lineHeight: '1.6' }}>
            We sent a 6-digit code to<br />
            <strong style={{ color: '#00d4a1' }}>{email}</strong>
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '28px' }}>
          {otp.map((d, i) => (
            <input key={i} id={'otp-' + i} value={d} onChange={e => handleChange(i, e.target.value)} onKeyDown={e => handleKeyDown(i, e)} maxLength={1} autoFocus={i === 0}
              style={{ width: '48px', height: '56px', textAlign: 'center', fontSize: '22px', fontWeight: '800', fontFamily: 'monospace', background: 'rgba(232,234,240,0.04)', border: d ? '2px solid #00d4a1' : '1px solid rgba(232,234,240,0.1)', borderRadius: '10px', color: '#e8eaf0', outline: 'none', transition: 'border-color 0.15s' }}
            />
          ))}
        </div>
        <button onClick={handleVerify} disabled={loading}
          style={{ width: '100%', padding: '13px', background: 'linear-gradient(135deg, #00d4a1, #00b888)', color: '#080d1a', border: 'none', borderRadius: '10px', fontWeight: '700', fontSize: '15px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '16px', fontFamily: 'inherit' }}>
          {loading ? <><RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> Verifying...</> : <><CheckCircle size={16} /> Verify Code</>}
        </button>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'rgba(232,234,240,0.4)', cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit' }}>← Back to login</button>
          <button onClick={handleResend} disabled={resending} style={{ background: 'none', border: 'none', color: '#00d4a1', cursor: 'pointer', fontSize: '13px', fontWeight: '600', fontFamily: 'inherit' }}>{resending ? 'Sending...' : 'Resend code'}</button>
        </div>
        <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
      </div>
    </div>
  );
}

// ─── LOGIN SCREEN ─────────────────────────────────────────────
export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [otpReason, setOtpReason] = useState('two_factor');
  const { login } = useAuth();
  const navigate = useNavigate();
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const loggedInUser = await login(email, password);
      toast.success('Welcome back!');
      const isAdmin = loggedInUser?.role === 'SUPER_ADMIN';
      if (isAdmin) {
        navigate('/admin');
      } else {
        const urlParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
        const intent = urlParams.get('intent');
        const plan = urlParams.get('plan') || 'professional';
        if (intent === 'subscribe') {
          navigate('/billing?plan=' + plan + '&autocharge=true');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (err) {
      const data = err.response?.data;
      if (err.response?.status === 403 && data?.code === 'otp_required') {
        // The SERVER withheld the session and has already emailed a code (an account that never
        // entered its registration code, or one with 2FA on). Nothing has been stored, so closing
        // this screen leaves the visitor signed out: the code is the only way in.
        setOtpReason(data.reason === 'verify_email' ? 'verify_email' : 'two_factor');
        toast.success(data.reason === 'verify_email' ? 'Please verify your email. We sent you a code.' : 'Verification code sent!');
        setShowOTP(true);
        return;
      }
      toast.error(typeof data?.detail === 'string' ? data.detail : 'Invalid email or password');
    } finally { setLoading(false); }
  };

  if (showOTP) return (
    <OTPVerify
      email={email}
      onSuccess={(session) => {
        // First-time verification finishes registration, so it continues to onboarding exactly
        // like the Register page does; a 2FA login goes where a normal login goes.
        if (otpReason === 'verify_email') navigate('/onboarding');
        else navigate(session?.user?.role === 'SUPER_ADMIN' ? '/admin' : '/dashboard');
      }}
      onBack={() => setShowOTP(false)}
    />
  );

  return (
    <div style={{ minHeight: '100vh', background: '#080d1a', display: 'flex', fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=DM+Serif+Display&display=swap');
        * { box-sizing: border-box; }
        .login-input { width: 100%; padding: 11px 14px; background: rgba(232,234,240,0.04); border: 1px solid rgba(232,234,240,0.1); border-radius: 10px; color: #e8eaf0; font-size: 14px; font-family: inherit; outline: none; transition: border-color 0.2s; }
        .login-input:focus { border-color: rgba(0,212,161,0.5); background: rgba(0,212,161,0.03); }
        .login-input::placeholder { color: rgba(232,234,240,0.3); }
        .login-left-panel { display: flex; }
        @media (max-width: 768px) {
          .login-left-panel { display: none !important; }
          .login-right-panel { padding: 32px 24px !important; }
        }
        .login-btn { width: 100%; padding: 13px; border-radius: 10px; border: none; background: linear-gradient(135deg, #00d4a1, #00b888); color: #080d1a; font-size: 15px; font-weight: 700; cursor: pointer; font-family: inherit; transition: opacity 0.2s; }
        .login-btn:hover { opacity: 0.88; }
        .login-btn:disabled { opacity: 0.5; cursor: not-allowed; }
      `}</style>

      {/* ── Left panel ── */}
      <div className="login-left-panel" style={{ width: '420px', flexShrink: 0, background: 'linear-gradient(160deg, rgba(0,212,161,0.07) 0%, rgba(0,0,0,0) 60%)', borderRight: '1px solid rgba(232,234,240,0.06)', padding: '48px 40px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '64px', cursor: 'pointer' }} onClick={() => navigate('/')}>
            <ProTrackMark size={32} />
            <div style={{ fontSize: '18px', fontWeight: '800', color: '#fff', letterSpacing: '-0.3px' }}>
              ProTrack<span style={{ color: '#00d4a1' }}>NG</span>
            </div>
          </div>
          <h2 style={{ fontSize: '32px', fontWeight: '900', color: '#fff', lineHeight: '1.15', letterSpacing: '-1px', fontFamily: "'DM Serif Display', Georgia, serif", marginBottom: '16px' }}>
            Nigeria's Tender<br /><span style={{ color: '#00d4a1' }}>Intelligence</span><br />Platform
          </h2>
          <p style={{ fontSize: '14px', color: 'rgba(232,234,240,0.45)', lineHeight: '1.7', marginBottom: '40px' }}>
            Track government and corporate tender sources across Nigeria in real time. AI-matched opportunities. Never miss a bid.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {[
              { icon: Zap, label: 'AI Tender Matching', desc: 'Ranked by fit to your company profile' },
              { icon: Shield, label: 'Compliance Tracking', desc: 'Certificates, deadlines, HSE reports' },
              { icon: TrendingUp, label: 'Market Intelligence', desc: 'Spending trends across all agencies' },
            ].map(({ icon: Icon, label, desc }) => (
              <div key={label} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                <div style={{ width: '36px', height: '36px', flexShrink: 0, background: 'rgba(0,212,161,0.08)', border: '1px solid rgba(0,212,161,0.15)', borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon size={16} color="#00d4a1" />
                </div>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#e8eaf0', marginBottom: '2px' }}>{label}</div>
                  <div style={{ fontSize: '12px', color: 'rgba(232,234,240,0.4)' }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '32px', borderTop: '1px solid rgba(232,234,240,0.06)' }}>
          <span style={{ fontSize: '11px', color: 'rgba(232,234,240,0.3)' }}>Powered by</span>
          <StackLogo size={16} variant="white" />
          <span style={{ fontSize: '11px', color: 'rgba(232,234,240,0.4)', fontWeight: '600' }}>ProStack NG Technologies</span>
        </div>
      </div>

      {/* ── Right panel — form ── */}
      <div className="login-right-panel" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 40px' }}>
        <div style={{ width: '100%', maxWidth: '400px' }}>
          <div style={{ marginBottom: '36px' }}>
            <h1 style={{ fontSize: '28px', fontWeight: '800', color: '#fff', letterSpacing: '-0.5px', marginBottom: '6px' }}>Welcome back</h1>
            <p style={{ fontSize: '14px', color: 'rgba(232,234,240,0.45)' }}>Sign in to your tender dashboard</p>
          </div>
          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: 'rgba(232,234,240,0.5)', letterSpacing: '0.5px', textTransform: 'uppercase', display: 'block', marginBottom: '7px' }}>Email Address</label>
              <input className="login-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@company.com" required />
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '7px' }}>
                <label style={{ fontSize: '12px', fontWeight: '600', color: 'rgba(232,234,240,0.5)', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Password</label>
                <span onClick={() => navigate('/forgot-password')} style={{ fontSize: '12px', color: '#00d4a1', cursor: 'pointer', fontWeight: '600' }}>Forgot password?</span>
              </div>
              <div style={{ position: 'relative' }}>
                <input className="login-input" type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Your password" required style={{ paddingRight: '44px' }} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'rgba(232,234,240,0.4)', cursor: 'pointer', padding: '4px' }}>
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <button type="submit" className="login-btn" disabled={loading} style={{ marginTop: '8px' }}>
              {loading ? 'Signing in...' : 'Sign In →'}
            </button>
          </form>
          <p style={{ textAlign: 'center', marginTop: '28px', fontSize: '13px', color: 'rgba(232,234,240,0.4)' }}>
            Don't have an account?{' '}
            <span onClick={() => navigate('/register')} style={{ color: '#00d4a1', cursor: 'pointer', fontWeight: '600' }}>
              Start free 14-day trial
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}
